import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const AUTH = {
  headers: {
    Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
  },
};

function HomePage() {
  // Navbar dropdowns
  const [openDropdown, setOpenDropdown] = useState(null); // "teams" | "leaders" | "players" | null
  const teamsRef = useRef(null);
  const leadersRef = useRef(null);
  const playersRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOpenSection, setMobileOpenSection] = useState(null); // "teams" | "leaders" | "players" | null
  
  // Navbar search
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [navbarSearchTerm, setNavbarSearchTerm] = useState("");
  const [navbarResults, setNavbarResults] = useState([]);
  const [navbarSelectedIndex, setNavbarSelectedIndex] = useState(-1);
  const navbarInputRef = useRef(null);
  const navbarContainerRef = useRef(null);

  // Hero search
  const [heroSearchTerm, setHeroSearchTerm] = useState("");
  const [heroResults, setHeroResults] = useState([]);
  const [heroSelectedIndex, setHeroSelectedIndex] = useState(-1);
  const heroInputRef = useRef(null);
  const heroContainerRef = useRef(null);

  // Top Performers
  const [topPerformers, setTopPerformers] = useState({
    passing: [],
    rushing: [],
    receiving: [],
  });
  const [loadingLeaders, setLoadingLeaders] = useState(true);

// Add near the other state declarations
const [fantasyLeaders, setFantasyLeaders] = useState({
  qb: [],
  rb: [],
  wr: [],
});
const [loadingFantasy, setLoadingFantasy] = useState(true);

// Add this useEffect (alongside the top performers one)
useEffect(() => {
  const fetchFantasy = async () => {
    try {
      const res = await axios.get(
        "https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-fantasy-leaders-homePage?season=2026&limit=3",
        AUTH
      );
      setFantasyLeaders(res.data);
    } catch (err) {
      console.error("Fantasy leaders error:", err);
    } finally {
      setLoadingFantasy(false);
    }
  };
  fetchFantasy();
}, []);

const fantasyCategories = [
  { title: "Top QBs", key: "qb", label: "PPR" },
  { title: "Top RBs", key: "rb", label: "PPR" },
  { title: "Top WRs", key: "wr", label: "PPR" },
];

function CommonNinjaFeed() {
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdn.commoninja.com/sdk/latest/commonninja.js";
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      // optional cleanup
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div className="commonninja_component pid-570989da-4a9a-440c-aea5-6ee3947ba839" />
  );
}

// Close search + any dropdown when clicking outside
useEffect(() => {
  const handleClickOutside = (e) => {
    // Close expanded search
    if (
      navbarContainerRef.current &&
      !navbarContainerRef.current.contains(e.target)
    ) {
      setSearchExpanded(false);
      setNavbarResults([]);
      setNavbarSelectedIndex(-1);
    }

    // Close dropdowns
    if (
      teamsRef.current && !teamsRef.current.contains(e.target) &&
      leadersRef.current && !leadersRef.current.contains(e.target) &&
      playersRef.current && !playersRef.current.contains(e.target)
    ) {
      setOpenDropdown(null);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

  // ========== Navbar Search ==========
  useEffect(() => {
    if (!navbarSearchTerm.trim()) {
      setNavbarResults([]);
      setNavbarSelectedIndex(-1);
      return;
    }

    const search = async () => {
      try {
        const res = await axios.get(
          `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-players?search=${encodeURIComponent(
            navbarSearchTerm
          )}`,
          AUTH
        );
        setNavbarResults(res.data.slice(0, 12));
        setNavbarSelectedIndex(-1);
      } catch (err) {
        console.error("Navbar search error:", err);
      }
    };

    const timeout = setTimeout(search, 400);
    return () => clearTimeout(timeout);
  }, [navbarSearchTerm]);

  // Close navbar results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        navbarContainerRef.current &&
        !navbarContainerRef.current.contains(e.target)
      ) {
        setNavbarResults([]);
        setNavbarSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ========== Hero Search ==========
  useEffect(() => {
    if (!heroSearchTerm.trim()) {
      setHeroResults([]);
      setHeroSelectedIndex(-1);
      return;
    }

    const search = async () => {
      try {
        const res = await axios.get(
          `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-players?search=${encodeURIComponent(
            heroSearchTerm
          )}`,
          AUTH
        );
        setHeroResults(res.data.slice(0, 12));
        setHeroSelectedIndex(-1);
      } catch (err) {
        console.error("Hero search error:", err);
      }
    };

    const timeout = setTimeout(search, 400);
    return () => clearTimeout(timeout);
  }, [heroSearchTerm]);

  // Close hero results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        heroContainerRef.current &&
        !heroContainerRef.current.contains(e.target)
      ) {
        setHeroResults([]);
        setHeroSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

// Keyboard navigation – Navbar
useEffect(() => {
  const handleKeyDown = (e) => {
    if (!navbarResults.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setNavbarSelectedIndex((prev) =>
        Math.min(prev + 1, navbarResults.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setNavbarSelectedIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter" && navbarSelectedIndex >= 0) {
      const selected = navbarResults[navbarSelectedIndex];
      const path =
        selected.type === "team"
          ? `/team/${selected.abbreviation}`
          : `/player/${selected.bdl_id}`;
      window.location.href = path;
      setNavbarSearchTerm("");
      setNavbarResults([]);
    } else if (e.key === "Escape") {
      setNavbarResults([]);
      setNavbarSelectedIndex(-1);
    }
  };

  document.addEventListener("keydown", handleKeyDown);
  return () => document.removeEventListener("keydown", handleKeyDown);
}, [navbarResults, navbarSelectedIndex]);

// Keyboard navigation – Hero
useEffect(() => {
  const handleKeyDown = (e) => {
    if (!heroResults.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHeroSelectedIndex((prev) =>
        Math.min(prev + 1, heroResults.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHeroSelectedIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter" && heroSelectedIndex >= 0) {
      const selected = heroResults[heroSelectedIndex];
      const path =
        selected.type === "team"
          ? `/team/${selected.abbreviation}`
          : `/player/${selected.bdl_id}`;
      window.location.href = path;
      setHeroSearchTerm("");
      setHeroResults([]);
    } else if (e.key === "Escape") {
      setHeroResults([]);
      setHeroSelectedIndex(-1);
    }
  };

  document.addEventListener("keydown", handleKeyDown);
  return () => document.removeEventListener("keydown", handleKeyDown);
}, [heroResults, heroSelectedIndex]);

  // Load Top Performers
  useEffect(() => {
    const fetchLeaders = async () => {
      try {
        const [passRes, rushRes, recRes] = await Promise.all([
          axios.get(
            `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-season-leaders?stat=passing_yards`,
            AUTH
          ),
          axios.get(
            `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-season-leaders?stat=rushing_yards`,
            AUTH
          ),
          axios.get(
            `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-season-leaders?stat=receiving_yards`,
            AUTH
          ),
        ]);

        setTopPerformers({
          passing: passRes.data.slice(0, 3),
          rushing: rushRes.data.slice(0, 3),
          receiving: recRes.data.slice(0, 3),
        });
      } catch (err) {
        console.error("Leaders fetch error:", err);
      } finally {
        setLoadingLeaders(false);
      }
    };

    fetchLeaders();
  }, []);

  const categories = [
    { title: "Passing", key: "passing", stat: "passing_yards" },
    { title: "Rushing", key: "rushing", stat: "rushing_yards" },
    { title: "Receiving", key: "receiving", stat: "receiving_yards" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-sans overflow-x-hidden">
      {/* NAVBAR */}
      <nav className="fixed top-0 w-full bg-black/90 backdrop-blur-md z-50 border-b border-red-500/30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Left: Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/images/HOFLogoSquare.png"
              alt="HOF Logo"
              className="w-12 h-12 object-contain"
            />
            <div>
              <div className="text-2xl font-black tracking-tighter">
                HELMETS OFF
              </div>
              <div className="text-[10px] text-red-400 -mt-1">FOOTBALL</div>
            </div>
          </Link>

          <div className="flex-1 hidden md:block"></div>

{/* Right side */}
<div className="flex items-center gap-4">
  
  {/* Collapsible Search (works on all screen sizes) */}
  <div className="relative" ref={navbarContainerRef}>
    {!searchExpanded ? (
      <button
        onClick={() => {
          setSearchExpanded(true);
          setTimeout(() => navbarInputRef.current?.focus(), 50);
        }}
        className="p-2 text-zinc-400 hover:text-white transition"
        aria-label="Search"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </button>
    ) : (
      <div className="relative w-64 sm:w-72">
        <input
          ref={navbarInputRef}
          type="text"
          value={navbarSearchTerm}
          onChange={(e) => setNavbarSearchTerm(e.target.value)}
          placeholder="Search players..."
          className="w-full bg-zinc-900 border border-zinc-700 rounded-full py-3 px-5 pl-12 text-sm focus:outline-none focus:border-red-500"
          autoFocus
        />
        <span className="absolute left-4 top-3.5 text-zinc-400">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </span>

        {navbarResults.length > 0 && (
          <div className="absolute mt-2 w-full bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50 max-h-64 overflow-y-auto">
            {navbarResults.map((item, index) => (
              <Link
                key={`${item.type}-${item.bdl_id}`}
                to={item.type === "team" ? `/team/${item.abbreviation}` : `/player/${item.bdl_id}`}
                className={`block px-4 py-3 hover:bg-zinc-800 ${navbarSelectedIndex === index ? "bg-zinc-800" : ""}`}
                onClick={() => {
                  setNavbarSearchTerm("");
                  setNavbarResults([]);
                  setSearchExpanded(false);
                }}
              >
                {item.type === "team" ? (
                  <>
                    <span className="font-medium">{item.name}</span>
                    <span className="text-zinc-500 text-sm"> • Team</span>
                  </>
                ) : (
                  <>
                    {item.first_name} {item.last_name} • {item.position_abbreviation}
                  </>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    )}
  </div>

  {/* Desktop Nav Links */}
  <div className="hidden md:flex items-center gap-8 text-sm font-medium">
    {/* TEAMS */}
    <div className="relative" ref={teamsRef}>
      <button
        onClick={() => setOpenDropdown(openDropdown === "teams" ? null : "teams")}
        className="hover:text-red-400 transition"
      >
        TEAMS
      </button>
      {openDropdown === "teams" && (
        <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
          <Link to="/teams" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>All Teams</Link>
          <Link to="/Standings" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Standings</Link>
          <Link to="/Schedule" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Schedule</Link>
        </div>
      )}
    </div>

    {/* LEADERS */}
    <div className="relative" ref={leadersRef}>
      <button
        onClick={() => setOpenDropdown(openDropdown === "leaders" ? null : "leaders")}
        className="hover:text-red-400 transition"
      >
        LEADERS
      </button>
      {openDropdown === "leaders" && (
        <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
          <Link to="/leaderboards" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>Season Leaders</Link>
          <Link to="/leaderboards?stat=passing_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Passing</Link>
          <Link to="/leaderboards?stat=rushing_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Rushing</Link>
          <Link to="/leaderboards?stat=receiving_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Receiving</Link>
        </div>
      )}
    </div>

    {/* PLAYERS */}
    <div className="relative" ref={playersRef}>
      <button
        onClick={() => setOpenDropdown(openDropdown === "players" ? null : "players")}
        className="hover:text-red-400 transition"
      >
        PLAYERS
      </button>
      {openDropdown === "players" && (
        <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
          <Link to="/playerStats" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>Player Stats</Link>
          <Link to="/playerCompare" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Player compare</Link>
        </div>
      )}
    </div>
  </div>

  {/* Mobile Hamburger Button */}
  <button
    className="md:hidden p-2 text-zinc-400 hover:text-white"
    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
    aria-label="Menu"
  >
    {mobileMenuOpen ? (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    ) : (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    )}
  </button>
</div>

        </div>

{/* Mobile Menu - collapsible sections */}
{mobileMenuOpen && (
  <div className="md:hidden absolute top-full left-0 w-full bg-black/95 backdrop-blur-md border-b border-zinc-800 px-6 py-5 z-40">
    
    {/* TEAMS */}
    <div className="border-b border-zinc-800">
      <button
        onClick={() =>
          setMobileOpenSection(mobileOpenSection === "teams" ? null : "teams")
        }
        className="w-full flex items-center justify-between py-4 text-lg font-medium hover:text-red-400 transition"
      >
        TEAMS
        <span className="text-zinc-500 text-sm">
          {mobileOpenSection === "teams" ? "−" : "+"}
        </span>
      </button>

      {mobileOpenSection === "teams" && (
        <div className="pb-4 space-y-3 pl-3">
          <Link to="/teams" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            All Teams
          </Link>
          <Link to="/Standings" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Standings
          </Link>
          <Link to="/Schedule" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Schedule
          </Link>
        </div>
      )}
    </div>

    {/* LEADERS */}
    <div className="border-b border-zinc-800">
      <button
        onClick={() =>
          setMobileOpenSection(mobileOpenSection === "leaders" ? null : "leaders")
        }
        className="w-full flex items-center justify-between py-4 text-lg font-medium hover:text-red-400 transition"
      >
        LEADERS
        <span className="text-zinc-500 text-sm">
          {mobileOpenSection === "leaders" ? "−" : "+"}
        </span>
      </button>

      {mobileOpenSection === "leaders" && (
        <div className="pb-4 space-y-3 pl-3">
          <Link to="/leaderboards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Season Leaders
          </Link>
          <Link to="/leaderboards?stat=passing_yards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Passing
          </Link>
          <Link to="/leaderboards?stat=rushing_yards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Rushing
          </Link>
          <Link to="/leaderboards?stat=receiving_yards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Receiving
          </Link>
        </div>
      )}
    </div>

    {/* PLAYERS */}
    <div>
      <button
        onClick={() =>
          setMobileOpenSection(mobileOpenSection === "players" ? null : "players")
        }
        className="w-full flex items-center justify-between py-4 text-lg font-medium hover:text-red-400 transition"
      >
        PLAYERS
        <span className="text-zinc-500 text-sm">
          {mobileOpenSection === "players" ? "−" : "+"}
        </span>
      </button>

      {mobileOpenSection === "players" && (
        <div className="pb-4 space-y-3 pl-3">
          <Link to="/playerStats" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Player Stats
          </Link>
          <Link to="/playerCompare" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Player Comparison
          </Link>
         </div>
      )}
    </div>

  </div>
)}
      </nav>



      {/* HERO */}
      <div className="relative h-screen flex items-center justify-center pt-16 bg-[radial-gradient(at_center,#1a1a1a_0%,#000000_70%)]">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(/images/HelmetHome2.jpg)`,
            opacity: 0.35,
          }}
        ></div>

        <div className="relative z-10 text-center px-6 max-w-5xl">
          <h1 className="text-7xl md:text-8xl font-black tracking-tighter mb-6 leading-none">
            HELMETS OFF
            <br />
            <span className="bg-gradient-to-r from-red-500 via-white to-blue-500 bg-clip-text text-transparent">
              GAME ON
            </span>
          </h1>

          <p className="text-lg text-zinc-400 max-w-lg mx-auto mb-10">
            Real-time NFL stats • Real Leaderboards • Real Ball Knowledge
          </p>

{/* Hero Search Container */}
<div className="max-w-md mx-auto relative" ref={heroContainerRef}>
  <input
    ref={heroInputRef}
    type="text"
    value={heroSearchTerm}
    onChange={(e) => setHeroSearchTerm(e.target.value)}
    placeholder="Search players or teams..."
    className="w-full bg-zinc-900 border border-zinc-700 rounded-full py-4 px-6 pl-14 text-base focus:outline-none focus:border-red-500"
  />
  
  {/* Modern magnifying glass */}
  <span className="absolute left-5 top-4 text-zinc-400">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="w-6 h-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  </span>

  {heroResults.length > 0 && (
    <div className="absolute mt-2 w-full bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50 max-h-64 overflow-y-auto">
      {heroResults.map((item, index) => (
        <Link
          key={`${item.type}-${item.bdl_id}`}
          to={item.type === "team" ? `/team/${item.abbreviation}` : `/player/${item.bdl_id}`}
          className={`block px-4 py-3 hover:bg-zinc-800 ${
            heroSelectedIndex === index ? "bg-zinc-800" : ""
          }`}
          onClick={() => {
            setHeroSearchTerm("");
            setHeroResults([]);
          }}
        >
          {item.type === "team" ? (
            <>
              <span className="font-medium">{item.name}</span>
              <span className="text-zinc-500 text-sm"> • Team</span>
            </>
          ) : (
            <>
              {item.first_name} {item.last_name} • {item.position_abbreviation}
            </>
          )}
        </Link>
      ))}
    </div>
  )}
</div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce text-4xl">
          ↓
        </div>
      </div>

{/* BOTTOM SECTION - 3 COLUMNS */}
<div className="w-[80%] mx-auto px-4 py-20">
  <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr_1fr] gap-8">
    
    {/* ===== LEFT COLUMN - Top Performers ===== */}
    <div className="space-y-8">
      <h2 className="text-3xl font-black text-center lg:text mb-2">
        TOP PERFORMERS
      </h2>

      {categories.map((cat) => (
        <div
          key={cat.key}
          className="bg-gradient-to-br from-zinc-900 to-black border border-zinc-700 hover:border-red-500 rounded-3xl p-8 transition-all"
        >
          <div className="text-red-500 font-mono text-xs tracking-widest mb-5">
            {cat.title.toUpperCase()} YARDS
          </div>

          <div className="space-y-6">
            {loadingLeaders ? (
              <p className="text-zinc-500 text-sm">Loading...</p>
            ) : (
              topPerformers[cat.key].map((p) => (
                <Link
                  key={p.player_id}
                  to={`/player/${p.player_id}`}
                  className="flex justify-between items-end border-b border-zinc-800 pb-5 last:border-0 last:pb-0 hover:text-red-400 transition"
                >
                  <div>
                    <div className="font-bold text-lg">{p.name}</div>
                    <div className="text-xs text-zinc-500">
                      {p.abbreviation} • {p.position}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black text-white/90">
                      {p[cat.stat] || 0}
                    </div>
                    <div className="text-[10px] text-zinc-500">YDS</div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      ))}
    </div>

{/* ===== CENTER COLUMN - Curator X Feed ===== */}
<div className="bg-zinc-900 border border-zinc-700 rounded-3xl p-6 flex flex-col h-full max-h-[1125px]">

  {/*<div className="text-red-500 font-mono text-xs tracking-widest mb-4 shrink-0">
    LIVE FROM X
  </div>
   <div id="curator-feed-default-feed-layout">
      <a
        href="https://curator.io"
        target="_blank"
        rel="noopener noreferrer"
        className="crt-logo crt-tag"
      >
        Powered by Curator.io
      </a>
    </div>
  
  */}
  <h2 className="text-3xl font-black text-center lg:text mb-2">
        LIVE FROM X
      </h2>
  <div className="flex-1 overflow-y-auto rounded-2xl bg-black/40 scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent">
   <div className="w-full max-w-2xl mx-auto">
  <CommonNinjaFeed />
</div>

  </div>
</div>

{/* ===== RIGHT COLUMN - Fantasy Leaders ===== */}
<div className="space-y-8">
  <h2 className="text-3xl font-black text-center lg:text mb-2">
    FANTASY LEADERS
  </h2>

  {fantasyCategories.map((cat) => (
    <div
      key={cat.key}
      className="bg-gradient-to-br from-zinc-900 to-black border border-zinc-700 hover:border-red-500 rounded-3xl p-8 transition-all"
    >
      <div className="text-red-500 font-mono text-xs tracking-widest mb-5">
        {cat.title.toUpperCase()} • {cat.label}
      </div>

      <div className="space-y-6">
        {loadingFantasy ? (
          <p className="text-zinc-500 text-sm">Loading...</p>
        ) : fantasyLeaders[cat.key]?.length ? (
          fantasyLeaders[cat.key].map((p) => (
            <Link
              key={p.player_id}
              to={`/player/${p.player_id}`}
              className="flex justify-between items-end border-b border-zinc-800 pb-5 last:border-0 last:pb-0 hover:text-red-400 transition"
            >
              <div>
                <div className="font-bold text-lg">{p.name}</div>
                <div className="text-xs text-zinc-500">
                  {p.abbreviation} • {p.position}
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-black text-white/90">
                  {Number(p.points).toFixed(1)}
                </div>
                <div className="text-[10px] text-zinc-500">PPR</div>
              </div>
            </Link>
          ))
        ) : (
          <p className="text-zinc-500 text-sm">No data</p>
        )}
      </div>
    </div>
  ))}
</div>

{/* ===== RIGHT COLUMN - Draft Center (commented out for offseason toggle) =====
<div className="space-y-8">
  <h2 className="text-3xl font-black text-center lg:text mb-2">
    DRAFT CENTER
  </h2>
  ... entire original Draft Center block ...
</div>
*/}

  </div>
</div>

      {/* FOOTER */}
      <footer className="bg-black border-t border-zinc-800 py-16">
        <div className="max-w-7xl mx-auto px-6 text-center text-zinc-500">
          <p>© 2026 Helmets Off Football • Serratox</p>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;